"use client";

import type { ReactNode } from "react";
import { type ExternalToast, toast as sonnerToast } from "sonner";

type ToastVariant = "default" | "destructive" | "success" | "warning" | "info";

type ToastOptions = {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  config?: ExternalToast;
};

type ToastReturn = string | number;

export function toast(options: ToastOptions): ToastReturn {
  const { title, description, variant = "default", config = {} } = options;

  const classes = {
    default: "!bg-background !text-foreground !border-border",
    destructive: "!bg-destructive !text-white !border-none",
    success: "!bg-background !text-foreground !border-border",
    warning: "!bg-background !text-foreground !border-border",
    info: "!bg-background !text-foreground !border-border",
  };

  const toastConfig = {
    className: classes[variant],
    ...config,
    description,
  };

  switch (variant) {
    case "destructive":
      return sonnerToast.error(title, toastConfig);
    case "success":
      return sonnerToast.success(title, toastConfig);
    case "warning":
      return sonnerToast.warning(title, toastConfig);
    case "info":
      return sonnerToast.info(title, toastConfig);
    default:
      return sonnerToast(title, toastConfig);
  }
}

type UseToastReturn = {
  toast: typeof toast;
  dismiss: typeof sonnerToast.dismiss;
  toasts: readonly never[];
};

export function useToast(): UseToastReturn {
  return {
    toast,
    dismiss: sonnerToast.dismiss,
    toasts: [],
  };
}

export type { ToastOptions as Toast };
